import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { useListQuery } from "../../lib/useListQuery";
import { reviewMarkerRows, type ReviewSummary } from "./reviewListData";

type Request = Omit<
  components["schemas"]["MarkerReviewSummaryRequest"],
  "page_size" | "page_token"
>;

export function useReviewListData(request: Request, enabled: boolean) {
  const scope = {
    agent_id: request.agent_id ?? undefined,
    team_id: request.team_id ?? undefined,
  };
  const markers = useListQuery("/v1/markers", {
    enabled,
    params: scope,
    queryKey: queryKeys.markers.list(scope),
  });
  const versions = useListQuery("/v1/agent-versions", {
    enabled,
    params: scope,
    queryKey: queryKeys.agentVersions.list(scope),
  });
  useEffect(() => {
    for (const list of [markers, versions]) {
      if (list.hasNextPage && !list.isFetchingNextPage && !list.errorMessage) {
        list.fetchNextPage();
      }
    }
  }, [markers, versions]);
  const summary = useQuery({
    enabled,
    queryKey: queryKeys.markers.reviewSummary(request),
    queryFn: async ({ signal }) => {
      const items: ReviewSummary[] = [];
      let pageToken: string | undefined;
      // The endpoint orders by identity. Exhaust its opaque cursors before
      // sorting so the most urgent marker is first across the whole scope.
      do {
        const { data, error } = await apiClient.POST(
          "/v1/markers:review-summary",
          {
            signal,
            body: { ...request, page_size: 200, page_token: pageToken ?? null },
          },
        );
        if (error) throw new ApiRequestError(error);
        items.push(...data.items);
        pageToken = data.next_page_token ?? undefined;
      } while (pageToken);
      return items;
    },
  });
  const { time_range, ...filters } = request;
  const scoreRequest = {
    time_range,
    filters: { ...filters, marker_id: null },
    human_side: "latest" as const,
    include_disagreements: false,
    disagreement_limit: 50,
    include_rater_agreement: false,
    rater_disagreement_limit: 50,
  };
  const score = useQuery({
    enabled,
    queryKey: queryKeys.alignment.list(scoreRequest),
    queryFn: async ({ signal }) => {
      const { data, error } = await apiClient.POST("/v1/alignment:score", {
        signal,
        body: scoreRequest,
      });
      if (error) throw new ApiRequestError(error);
      return data;
    },
  });
  const rows = useMemo(
    () =>
      reviewMarkerRows(
        summary.data ?? [],
        markers.items,
        score.data?.by_marker ?? [],
      ),
    [summary.data, markers.items, score.data],
  );
  return {
    rows,
    versions,
    isPending:
      summary.isPending ||
      score.isPending ||
      markers.isPending ||
      markers.hasNextPage,
    error:
      markers.errorMessage ??
      (summary.isError ? requestErrorDescription(summary.error) : null) ??
      (score.isError ? requestErrorDescription(score.error) : null),
    notice: score.data?.notice,
  };
}
